import {
  baseballLogicService,
  CallTypes,
  HitTypes,
  IDataGridColumnConfiguration,
  KOrBB,
  ResultTypes,
} from '@trackman/baseball-shared-components';
import { IDataGridError } from '@trackman/baseball-shared-components/build/models/DataGrid/IDataGridError';
import { IDataGridValidator } from '@trackman/baseball-shared-components/build/models/DataGrid/IDataGridValidator';
import { EditRowColumnName } from 'enums/EditRowColumnName';
import { PlayRow } from 'models/PlayEditRow/PlayRow';
import { EditRowDataKey } from '../../enums/EditRowDataKey';

export const baseballLogicValidator: IDataGridValidator = {
  validate: (rows: PlayRow[], columns: IDataGridColumnConfiguration[]) => {
    const result: IDataGridError[][] = [];
    let prevLivePlayIndex = -1;
    for (let i = 0; i < rows.length; i++) {
      const errors: IDataGridError[] = [];
      const row = rows[i];
      if (!row.isWarmup && !row.isDeleted) {
        const prevRow = prevLivePlayIndex >= 0 ? rows[prevLivePlayIndex] : undefined;
        validateLogic(row, prevRow, errors);
        prevLivePlayIndex = i;
      }
      result.push(errors);
    }
    return result;
  },
};

const validateLogic = (row: PlayRow, prevRow: PlayRow | undefined, errors: IDataGridError[]) => {
  if (row.outs > 2) {
    errors.push({
      dataKey: EditRowDataKey.Outs,
      errorMessage: 'Outs > 2',
    });
  }
  if (row.playResult === ResultTypes.Out && row.outsOnPlay === 0) {
    errors.push({
      dataKey: EditRowDataKey.OutsOnPlay,
      errorMessage: `No ${EditRowColumnName.OutsOnPlay} but ${EditRowColumnName.PlayResult} is ${ResultTypes.Out}`,
    });
  }
  if (row.balls > 3) {
    errors.push({
      dataKey: EditRowDataKey.Balls,
      errorMessage: 'Balls > 3',
    });
  }
  if (row.strikes > 2) {
    errors.push({
      dataKey: EditRowDataKey.Strikes,
      errorMessage: 'Strikes > 2',
    });
  }
  if (prevRow && baseballLogicService.isBall(prevRow.pitchCall) && prevRow.balls >= row.balls && prevRow.batter.name === row.batter.name) {
    errors.push({
      dataKey: EditRowDataKey.Balls,
      errorMessage: `${EditRowColumnName.Balls} error`,
    });
  }

  if (prevRow && baseballLogicService.isBall(prevRow.pitchCall) && row.strikes > prevRow.strikes) {
    errors.push({
      dataKey: EditRowDataKey.Strikes,
      errorMessage: `${EditRowColumnName.Strikes} error`,
    });
  }

  if (
    prevRow &&
    baseballLogicService.isStrike(prevRow.pitchCall, prevRow.strikes) &&
    prevRow.strikes >= row.strikes &&
    prevRow.batter.name === row.batter.name
  ) {
    errors.push({
      dataKey: EditRowDataKey.Strikes,
      errorMessage: `${EditRowColumnName.Strikes} error`,
    });
  }

  if (prevRow && baseballLogicService.isStrike(prevRow.pitchCall, 0) && row.balls > prevRow.balls) {
    errors.push({
      dataKey: EditRowDataKey.Balls,
      errorMessage: `${EditRowColumnName.Balls} error`,
    });
  }

  if (row.pitchCall !== CallTypes.InPlay && row.hitType !== HitTypes.Undefined) {
    errors.push({
      dataKey: EditRowDataKey.HitType,
      errorMessage: `${EditRowColumnName.PitchCall} not '${CallTypes.InPlay}', but ${EditRowColumnName.HitType}: '${row.hitType}'`,
    });
  }

  if (baseballLogicService.isBall(row.pitchCall) && row.pitchCall !== CallTypes.HitByPitch && row.balls === 3 && row.korbb !== KOrBB.Walk) {
    errors.push({
      dataKey: EditRowDataKey.KOrBB,
      errorMessage: `${EditRowColumnName.KOrBB} error`,
    });
  }
  if (
    row.pitchCall !== CallTypes.BallIntentional &&
    row.korbb === KOrBB.Walk &&
    (!baseballLogicService.isBall(row.pitchCall) || row.balls < 3)
  ) {
    errors.push({
      dataKey: EditRowDataKey.KOrBB,
      errorMessage: `Walk error`,
    });
  }

  if (baseballLogicService.isStrike(row.pitchCall, row.strikes) && row.strikes === 2 && row.korbb !== KOrBB.Strikeout) {
    errors.push({
      dataKey: EditRowDataKey.KOrBB,
      errorMessage: `${EditRowColumnName.KOrBB} error'`,
    });
  }

  if (
    prevRow &&
    prevRow.korbb === KOrBB.Strikeout &&
    row.outs <= prevRow.outs + prevRow.outsOnPlay &&
    prevRow.inning.inningNumber === row.inning.inningNumber &&
    prevRow.inning.isTopInning === row.inning.isTopInning
  ) {
    errors.push({
      dataKey: EditRowDataKey.Outs,
      errorMessage: `${EditRowColumnName.Outs} not increased, but prev. pitch was a ${KOrBB.Strikeout}`,
    });
  }

  if (prevRow && (prevRow.pitchCall === CallTypes.InPlay || prevRow.pitchCall === CallTypes.HitByPitch)) {
    if (prevRow.batter.id === row.batter.id) {
      errors.push({
        dataKey: EditRowDataKey.Batter,
        errorMessage: 'Batter not changed',
      });
    }

    if (row.balls !== 0) {
      errors.push({
        dataKey: EditRowDataKey.Balls,
        errorMessage: `${EditRowColumnName.Balls} error`,
      });
    }

    if (row.strikes !== 0) {
      errors.push({
        dataKey: EditRowDataKey.Strikes,
        errorMessage: `${EditRowColumnName.Strikes} error`,
      });
    }
  }

  if (
    prevRow &&
    prevRow.outsOnPlay > 0 &&
    row.outs <= prevRow.outs &&
    row.inning.inningNumber === prevRow.inning.inningNumber &&
    row.inning.isTopInning === prevRow.inning.isTopInning
  ) {
    errors.push({
      dataKey: EditRowDataKey.Outs,
      errorMessage: 'Outs error',
    });
  }
};
